import {
  ActionIcon,
  Button,
  Checkbox,
  Group,
  HoverCard,
  Input,
  Loader,
  Table,
  Tooltip,
} from '@mantine/core'
import type { Filter } from '@medplum/core'
import { logError } from '@/utils/errorUtils'
import { useMedplum } from '@medplum/react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { showNotification } from '@mantine/notifications'
import InfiniteScroll from 'react-infinite-scroll-component'
import { FieldsMenu, FilterMenu, Th } from '@/components'
import { useDebounce, useMiddlewareClient } from '@/hooks'
import { useClient } from '@/hooks/useClient'
import {
  useDuplicateReviewCounts,
  useFindDuplicateReviewForPatient,
  useOpenDuplicateReview,
} from '@/hooks/usePatientMerge'
import { useNavigate } from 'react-router-dom'
import { IconAlertTriangle, IconPlus, IconSearch, IconUsers, IconX } from '@tabler/icons-react'
import AddNewPatientModal from '@/components/ui/CreateUpdatePatientModal/CreateUpdatePatientModal'
import { formatDateTime, formatPatientName, formatPhone } from '@/utils.ts'
import { modals } from '@mantine/modals'
import { PatientStatusBadge } from '@/components/ui/UserPortalStatus/PatientStatusBadge'
import { AssignOrganizationModal } from './components/AssignOrganizationModal'
import {
  buildPatientFilter,
  isSortable,
  orderFor,
  PATIENT_LIST_INCLUDE,
  type OrderDir,
  type PatientRow,
} from './patientListQuery'

const PAGE_SIZE = 50

// Column order follows the redesign spec. `portal-status` is a synthetic column (no field code);
// every other column maps to a PatientRow field. Codes match the legacy FHIR field codes so the
// Fields menu and sort mappings stay recognizable.
const ALL_COLUMNS = [
  { code: 'photo', title: 'Photo' },
  { code: 'name', title: 'Name' },
  { code: 'portal-status', title: 'Status' },
  { code: 'gender', title: 'Gender' },
  { code: 'birthdate', title: 'Birth Date' },
  { code: 'phone', title: 'Phone' },
  { code: 'email', title: 'Email' },
  { code: 'address-line', title: 'Address' },
  { code: 'address-city', title: 'City' },
  { code: 'address-state', title: 'State' },
  { code: 'address-postalcode', title: 'Postal Code' },
] as const

// Status is computed and always shown; the rest can be toggled from the Fields menu.
const TOGGLEABLE_COLUMNS = ALL_COLUMNS.filter((c) => c.code !== 'portal-status')

// Palette for the initials avatar shown when a patient has no photo. Each patient gets a stable color
// picked from their name, rendered as solid text on a light tint of the same color (hex `26` ≈ 15%).
const AVATAR_COLORS = [
  '#E5533D',
  '#1971C2',
  '#2F9E44',
  '#9C36B5',
  '#E8590C',
  '#0CA678',
  '#4263EB',
  '#C2255C',
  '#F08C00',
  '#495057',
] as const

function avatarColor(seed: string): string {
  let hash = 0
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0
  return AVATAR_COLORS[hash % AVATAR_COLORS.length]
}

function patientDisplayName(patient: PatientRow): string {
  return formatPatientName(
    { family: patient.family ?? undefined, given: patient.given ? [patient.given] : undefined },
    true,
  )
}

function patientInitials(given: string | null, family: string | null): string {
  const initials = (given?.trim().charAt(0) ?? '') + (family?.trim().charAt(0) ?? '')
  return initials.toUpperCase() || '?'
}

export const PatientList = () => {
  const medplum = useMedplum()
  const navigate = useNavigate()
  const middleware = useMiddlewareClient()
  const client = useClient()

  const [rows, setRows] = useState<PatientRow[]>([])
  const [totalCount, setTotalCount] = useState<number | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [offset, setOffset] = useState(0)
  const [isLoading, setIsLoading] = useState(true)

  const searchInputRef = useRef<HTMLInputElement>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearchQuery = useDebounce(searchQuery, 500)

  const [filters, setFilters] = useState<Filter[]>([])
  const [sort, setSort] = useState<{ code: string; dir: OrderDir } | null>(null)
  const [visibleFields, setVisibleFields] = useState<string[]>(
    TOGGLEABLE_COLUMNS.map((c) => c.code),
  )

  const [isAddPatientModalOpen, setIsAddPatientModalOpen] = useState(false)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [refreshKey, setRefreshKey] = useState(0)
  const [assignTarget, setAssignTarget] = useState<PatientRow | null>(null)
  // Only one toolbar popup (Fields / Filters) may be open at a time.
  const [openMenu, setOpenMenu] = useState<'fields' | 'filter' | null>(null)
  const hasAppliedFilter = filters.some((f) => f.code && f.operator && f.value)

  const isSuperAdmin = medplum.isSuperAdmin()
  // Duplicate review is for every practitioner (care coordinators and clinicians); delete stays
  // super-admin only.
  const duplicateCounts = useDuplicateReviewCounts()
  const openReview = useOpenDuplicateReview()
  const findReviewForPatient = useFindDuplicateReviewForPatient()
  const duplicatePatientIds = useMemo(
    () => new Set(duplicateCounts.data?.openPatientIds ?? []),
    [duplicateCounts.data],
  )
  const filtersKey = useMemo(() => JSON.stringify(filters), [filters])
  const sortKey = sort ? `${sort.code}:${sort.dir}` : ''
  const prevQueryKeyRef = useRef<string | null>(null)

  // Fetch a page from /v2/patients. Changing the query (search / filters / sort / refresh) resets the
  // offset to 0 first — mirroring the previous behavior — so a fresh result set replaces the list; a
  // growing offset appends the next page for infinite scroll.
  useEffect(() => {
    const queryKey = `${debouncedSearchQuery}|${filtersKey}|${sortKey}|${refreshKey}`
    const queryChanged = prevQueryKeyRef.current !== null && prevQueryKeyRef.current !== queryKey
    prevQueryKeyRef.current = queryKey
    if (queryChanged && offset !== 0) {
      setOffset(0)
      return
    }

    let cancelled = false
    setIsLoading(true)
    client
      .listPatients({
        page: { offset, count: PAGE_SIZE, limit: Number.MAX_SAFE_INTEGER },
        filter: buildPatientFilter(debouncedSearchQuery, filters),
        order: sort ? orderFor(sort.code, sort.dir) : undefined,
        include: [...PATIENT_LIST_INCLUDE],
      })
      .then((res) => {
        if (cancelled) return
        setRows((prev) => (offset === 0 ? res.data : [...prev, ...res.data]))
        setTotalCount(res.page.total)
        const loaded = offset + res.data.length
        setHasMore(
          !res.page.truncated && (Boolean(res.page.incompleteTotal) || loaded < res.page.total),
        )
      })
      .catch((e) => {
        if (cancelled) return
        logError('patient', 'Failed to fetch patients', e)
        showNotification({ title: 'Error', message: 'Failed to fetch patients', color: 'red' })
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, debouncedSearchQuery, filtersKey, sortKey, offset, refreshKey])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return
      const target = e.target as HTMLElement | null
      const isTyping =
        target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.isContentEditable
      if (isTyping) return
      e.preventDefault()
      searchInputRef.current?.focus()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  const loadMore = () => setOffset((o) => o + PAGE_SIZE)

  const handleSelectOne = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  const deletePatient = async (patientId: string) => {
    try {
      setIsLoading(true)
      await middleware.post('/user/remove', { userId: patientId })
      setRows((prev) => prev.filter((item) => item.id !== patientId))
      setSelectedIds((prev) => prev.filter((id) => id !== patientId))
      setTotalCount((prev) => (prev !== null ? Math.max(0, prev - 1) : prev))
      showNotification({ color: 'green', message: 'Patient deleted successfully' })
    } catch (e) {
      logError('patient', 'Failed to delete patient', e)
      showNotification({ color: 'red', message: 'Something went wrong' })
    } finally {
      setIsLoading(false)
    }
  }

  const handleCompareSelected = () => {
    if (selectedIds.length !== 2) return
    openReview.mutate(
      { patientIds: [selectedIds[0], selectedIds[1]] },
      { onSuccess: ({ reviewId }) => void navigate(`/patients/duplicates/${reviewId}`) },
    )
  }

  const handleReviewDuplicate = async (patientId: string) => {
    try {
      const reviewId = await findReviewForPatient(patientId)
      if (reviewId) void navigate(`/patients/duplicates/${reviewId}`)
      else showNotification({ message: 'This record has no open duplicate review.' })
    } catch (e) {
      logError('patient', 'Failed to open duplicate review', e)
      showNotification({ color: 'red', message: 'Could not open the review. Please try again.' })
    }
  }

  const handleDeleteSelected = () => {
    if (selectedIds.length === 0) return
    modals.openConfirmModal({
      title: 'Delete selected patients',
      children: `Are you sure you want to delete ${selectedIds.length} patients?`,
      confirmProps: { color: 'red', children: 'Delete' },
      onConfirm: () => {
        void (async () => {
          for (const id of [...selectedIds]) {
            await deletePatient(id)
          }
          setSelectedIds([])
        })()
      },
    })
  }

  const onSort = (code: string): (() => void) | undefined => {
    if (!isSortable(code)) return undefined
    return () =>
      setSort((prev) => {
        if (!prev || prev.code !== code) return { code, dir: 'desc' }
        if (prev.dir === 'desc') return { code, dir: 'asc' }
        return null
      })
  }

  const renderColumn = (patient: PatientRow, itemCode: string) => {
    switch (itemCode) {
      case 'photo': {
        const color = avatarColor(
          `${patient.given ?? ''} ${patient.family ?? ''}`.trim() || patient.id,
        )
        return (
          <div className="flex items-center gap-2">
            {patient.photo ? (
              <img
                src={patient.photo}
                alt="patient-photo"
                className="h-[30px] w-[30px] rounded-full object-cover"
              />
            ) : (
              <div
                className="flex h-[30px] w-[30px] items-center justify-center rounded-full text-[11px] font-semibold"
                style={{ color, backgroundColor: `${color}26` }}
              >
                {patientInitials(patient.given, patient.family)}
              </div>
            )}
          </div>
        )
      }
      case 'portal-status':
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <PatientStatusBadge
              patientId={patient.id}
              status={patient.status}
              lastLogin={patient.lastLogin}
              lastInvitationSent={patient.lastInvitationSent}
              withResendInvitationButton
            />
          </div>
        )
      case 'name':
        return (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-semibold text-[#1F2733]">{patientDisplayName(patient)}</span>
            {duplicatePatientIds.has(patient.id) && (
              // A HoverCard, not a Tooltip: the pointer has to reach the Review link inside it.
              // The badge opens the review too, for touch and keyboard. The dropdown is portaled
              // but React still bubbles its clicks to the row, so it stops them as well.
              <HoverCard position="top" openDelay={100} closeDelay={150} shadow="sm">
                <HoverCard.Target>
                  <button
                    type="button"
                    aria-label="Possible duplicate"
                    className="inline-flex size-[22px] shrink-0 cursor-pointer items-center justify-center rounded-full border-none bg-[#FFF4E0] p-0 text-[#B26A00] transition hover:bg-[#FDE7BF]"
                    onClick={(e) => {
                      e.stopPropagation()
                      void handleReviewDuplicate(patient.id)
                    }}
                  >
                    <IconAlertTriangle size={14} stroke={2} aria-hidden />
                  </button>
                </HoverCard.Target>
                <HoverCard.Dropdown
                  className="!rounded-full !border-none !bg-[#FFF4E0] !px-3 !py-1.5"
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#B26A00]">
                    <IconAlertTriangle size={14} stroke={2} aria-hidden />
                    <span>Possible duplicate</span>
                    <button
                      type="button"
                      className="cursor-pointer border-none bg-transparent p-0 text-[13px] font-semibold !text-[#E5533D] underline"
                      onClick={() => void handleReviewDuplicate(patient.id)}
                    >
                      Review
                    </button>
                  </span>
                </HoverCard.Dropdown>
              </HoverCard>
            )}
            {patient.unmatched && (
              <Tooltip
                label="No provider is licensed in this patient's state, so they cannot book"
                withArrow
              >
                <span className="inline-flex rounded-full bg-[#FDECE8] px-2 py-[3px] text-[11px] font-semibold text-[#C92A2A]">
                  No provider in state
                </span>
              </Tooltip>
            )}
          </div>
        )
      case 'gender':
        return patient.gender ?? undefined
      case 'birthdate':
        return formatDateTime(patient.birthday || '', '-', false, true)
      case 'phone':
        return patient.phone ? formatPhone(patient.phone) : undefined
      case 'email':
        return patient.email ?? undefined
      case 'address-line':
        return patient.address ?? undefined
      case 'address-city':
        return patient.city ?? undefined
      case 'address-state':
        return patient.state ?? undefined
      case 'address-postalcode':
        return patient.zip ?? undefined
      default:
        return itemCode
    }
  }

  const columns = ALL_COLUMNS.filter(
    (c) => c.code === 'portal-status' || visibleFields.includes(c.code),
  )

  const renderTableHeader = () =>
    columns.map((item) => {
      const active = sort?.code === item.code
      return (
        <Th
          className="text-[11px] font-semibold tracking-wider text-[#8A94A6] uppercase"
          key={item.code}
          reversed={active && sort?.dir === 'asc'}
          sorted={active}
          onSort={onSort(item.code)}
        >
          {item.title}
        </Th>
      )
    })

  return (
    <div className="box-border h-[calc(100vh-64px)] bg-[#F6F7F9] p-4 text-[#1F2733]">
      <div className="flex h-full flex-col gap-4 rounded-[18px] border border-[#E7E9EC] bg-white px-6 pt-6 pb-4 shadow-[0_1px_3px_rgba(16,24,40,.05),0_12px_30px_rgba(16,24,40,.05)]">
        <div className="flex w-full flex-wrap items-center gap-5">
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-2.5">
              <IconUsers size={22} className="text-[#E5533D]" stroke={2} />
              <h1 className="text-xl font-bold tracking-tight text-[#1F2733]">Patient list</h1>
              {totalCount !== null && (
                <span className="rounded-full bg-[#F1F2F4] px-2.5 py-0.5 text-sm font-semibold text-[#6B7280]">
                  {totalCount}
                </span>
              )}
            </div>
            {totalCount !== null && (
              <p className="text-sm font-medium text-[#6B7280]">
                Showing {rows.length} of {totalCount} patient{totalCount === 1 ? '' : 's'}
              </p>
            )}
          </div>

          <div className="ml-[20px] flex flex-wrap items-center gap-3">
            <Input
              ref={searchInputRef}
              placeholder="Search by name, phone or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.currentTarget.value)}
              radius="md"
              leftSection={<IconSearch size={16} className="text-[#9AA0A8]" />}
              rightSection={
                searchQuery ? (
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="sm"
                    aria-label="Clear search"
                    onClick={() => setSearchQuery('')}
                  >
                    <IconX size={14} />
                  </ActionIcon>
                ) : (
                  <kbd className="rounded border border-[#E4E7EB] bg-[#F4F6F8] px-1.5 text-xs font-medium text-[#9AA0A8]">
                    /
                  </kbd>
                )
              }
              rightSectionPointerEvents="all"
              className="w-full min-w-[200px] sm:w-[280px] md:w-[320px]"
              classNames={{
                input:
                  '!h-[32px] !min-h-[32px] !rounded-md !border-[#D8DBE0] !text-[16px] text-[#33383F]',
              }}
            />
            <FieldsMenu
              fields={TOGGLEABLE_COLUMNS.map((item) => ({ label: item.title, value: item.code }))}
              defaultChecked={visibleFields}
              onChange={setVisibleFields}
              opened={openMenu === 'fields'}
              onOpenChange={(o) => setOpenMenu(o ? 'fields' : null)}
            />
            <FilterMenu
              fields={TOGGLEABLE_COLUMNS.map((item) => ({ label: item.title, value: item.code }))}
              value={filters}
              onChange={setFilters}
              opened={openMenu === 'filter'}
              onOpenChange={(o) => setOpenMenu(o ? 'filter' : null)}
            />
            <button
              type="button"
              disabled={selectedIds.length !== 2 || openReview.isPending}
              aria-busy={openReview.isPending}
              onClick={handleCompareSelected}
              className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-md border border-[#D8DBE0] bg-white px-2.5 py-[3px] !text-[16px] !font-semibold text-[#33383F] transition hover:bg-[#F4F6F8] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white"
            >
              {openReview.isPending && <Loader size={14} color="gray" />}
              Compare / merge selected
            </button>
            <button
              type="button"
              onClick={() => void navigate('/patients/duplicates')}
              className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-md border border-[#D8DBE0] bg-white px-2.5 py-[3px] !text-[16px] !font-semibold text-[#33383F] transition hover:bg-[#F4F6F8]"
            >
              {duplicateCounts.data
                ? `Review duplicates · ${duplicateCounts.data.awaiting}`
                : 'Review duplicates'}
            </button>
            {hasAppliedFilter && (
              <button
                type="button"
                onClick={() => setFilters([])}
                className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-md border border-[#F3C6BD] bg-white px-2.5 py-[3px] !text-[16px] !font-semibold text-[#E5533D] transition hover:bg-[#FDECE8]"
              >
                <IconX size={16} stroke={2.5} />
                Clear filters
              </button>
            )}
          </div>

          <div className="ml-auto flex shrink-0 items-center">
            <button
              onClick={() => setIsAddPatientModalOpen(true)}
              className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-md border-none bg-[#E5533D] px-2.5 py-[3px] !text-[16px] !font-bold text-white shadow-[0_1px_2px_rgba(203,71,31,.3)] transition hover:bg-[#D4472F]"
            >
              <IconPlus size={16} stroke={2.5} />
              Add patient
            </button>
          </div>
        </div>

        <Group>
          {isSuperAdmin && (
            <Button
              variant="light"
              color="red"
              className="rounded-[5px] border border-red-200 bg-gradient-to-b from-white to-red-50 px-[15px] py-[5px] text-[#AB3131] shadow-sm hover:bg-red-50"
              disabled={selectedIds.length === 0}
              onClick={handleDeleteSelected}
            >
              Delete selected
            </Button>
          )}
          <span className="text-sm text-gray-500">{selectedIds.length} selected</span>
        </Group>

        <div className="flex-1 overflow-hidden">
          <div id="patient-table-scroll" className="h-full overflow-y-auto">
            <InfiniteScroll
              dataLength={rows.length}
              next={loadMore}
              hasMore={hasMore}
              loader={<Loader color="blue" />}
              scrollableTarget="patient-table-scroll"
              style={{ display: 'contents' }}
            >
              <Table className="w-full table-fixed">
                <Table.Thead className="bg-white" style={{ position: 'sticky', top: 0, zIndex: 5 }}>
                  <Table.Tr className="h-[40px] border-b border-[#EAECEF] !bg-white">
                    <Table.Th />
                    {renderTableHeader()}
                    {isSuperAdmin && (
                      <Table.Th className="text-[11px] font-semibold tracking-wider text-[#8A94A6] uppercase">
                        Actions
                      </Table.Th>
                    )}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rows.map((patient) => (
                    <Table.Tr
                      key={patient.id}
                      className="h-[52px] cursor-pointer border-b border-[#F0F1F3] text-[13px] text-[#4A5160] transition-colors hover:bg-[#FAFBFC]"
                      onClick={() => navigate(`/patients/${patient.id}`)}
                    >
                      <Table.Td onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedIds.includes(patient.id)}
                          onChange={() => handleSelectOne(patient.id)}
                          aria-label={`Select ${patientDisplayName(patient)}`}
                        />
                      </Table.Td>
                      {columns.map((item) => {
                        const content = renderColumn(patient, item.code)
                        return (
                          <Table.Td key={item.code}>
                            {content === undefined || content === '' ? (
                              <span className="text-[#B4B9C0]">—</span>
                            ) : (
                              content
                            )}
                          </Table.Td>
                        )
                      })}
                      {isSuperAdmin && (
                        <Table.Td>
                          <Group gap="xs" wrap="nowrap">
                            {patient.unmatched && (
                              <Button
                                variant="light"
                                color="red"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setAssignTarget(patient)
                                }}
                              >
                                Assign
                              </Button>
                            )}
                            <Button
                              variant="light"
                              color="red"
                              className="rounded-[5px] border border-red-200 bg-gradient-to-b from-white to-red-50 px-[15px] py-[5px] text-[#AB3131] shadow-sm hover:bg-red-50"
                              onClick={(e) => {
                                e.stopPropagation()
                                modals.openConfirmModal({
                                  children: 'Are you sure you want to remove this patient?',
                                  onConfirm: () => {
                                    void deletePatient(patient.id)
                                  },
                                  confirmProps: { color: 'red', children: 'Delete' },
                                })
                              }}
                            >
                              Delete
                            </Button>
                          </Group>
                        </Table.Td>
                      )}
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </InfiniteScroll>
          </div>
        </div>
      </div>

      <AddNewPatientModal
        isOpen={isAddPatientModalOpen}
        onClose={() => setIsAddPatientModalOpen(false)}
        successCallback={(patient) => {
          setRefreshKey((k) => k + 1)
          void navigate(`/patients/${patient.id}`)
        }}
      />
      {assignTarget && (
        <AssignOrganizationModal
          patientId={assignTarget.id}
          patientName={patientDisplayName(assignTarget)}
          opened
          onClose={() => setAssignTarget(null)}
          onAssigned={() => {
            setAssignTarget(null)
            setRefreshKey((k) => k + 1)
          }}
        />
      )}
      {isLoading && (
        <div className="absolute top-0 left-0 z-100 flex h-[100vh] w-[100vw] items-center justify-center bg-[rgba(255,255,255,0.5)]">
          <Loader />
        </div>
      )}
    </div>
  )
}
